import axios from 'axios'
import { resolveAppRoute } from '@/utils/path'
import { trackApiError } from '@/services/errorTracker'

const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null
const APP_VERSION_REFRESH_KEY = 'korkem-auto-force-refresh-version'
let appReloadScheduled = false

const cleanupLegacyClientCaches = async () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return

  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map(registration => registration.unregister()))
    } catch (_) {
      // Ignore service worker cleanup failures
    }
  }

  if ('caches' in window) {
    try {
      const cacheKeys = await caches.keys()
      await Promise.all(cacheKeys.map(key => caches.delete(key)))
    } catch (_) {
      // Ignore cache cleanup failures
    }
  }
}

export const triggerForcedClientRefresh = async (latestVersion = null) => {
  if (typeof window === 'undefined' || appReloadScheduled) return

  const currentVersion = appVersion ? String(appVersion) : null
  const normalizedLatestVersion = latestVersion ? String(latestVersion).trim() : null

  if (!currentVersion) {
    return
  }

  if (normalizedLatestVersion && normalizedLatestVersion === currentVersion) {
    return
  }

  const effectiveVersion = normalizedLatestVersion || `${currentVersion}-forced`
  const refreshMarker = `${currentVersion}->${effectiveVersion}`
  if (sessionStorage.getItem(APP_VERSION_REFRESH_KEY) === refreshMarker) {
    return
  }

  appReloadScheduled = true
  sessionStorage.setItem(APP_VERSION_REFRESH_KEY, refreshMarker)

  await cleanupLegacyClientCaches()

  const url = new URL(window.location.href)
  url.searchParams.set('appv', effectiveVersion)
  url.searchParams.set('refresh', Date.now().toString())
  window.location.replace(url.toString())
}

const api = axios.create({
  // Use /api to simulate production environment exactly and leverage Vite proxy
  baseURL: '/api',
  timeout: 3600000 // 1 hour for large video uploads
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    if (appVersion) {
      config.headers['X-App-Version'] = appVersion
    }
    // Add auth token if available (check sessionStorage first, then localStorage for "Remember Me")
    const token = sessionStorage.getItem('korkem-auto-token') || localStorage.getItem('korkem-auto-token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // If sending FormData, let the browser set proper multipart boundary
    if (config.data instanceof FormData) {
      if (config.headers && 'Content-Type' in config.headers) {
        delete config.headers['Content-Type']
      }
    }
    
    return config
  },
  (error) => {
    // Console log removed
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    const latestVersion = response?.headers?.['x-latest-app-version']
    if (latestVersion && appVersion && String(latestVersion).trim() !== String(appVersion).trim()) {
      triggerForcedClientRefresh(latestVersion)
    }
    return response
  },
  (error) => {
    // Mobile debugging for auth errors
    if (/Mobi|Android/i.test(navigator.userAgent) && 
        (error.config?.url?.includes('/auth/forgot-password') || error.config?.url?.includes('/auth/change-password'))) {
      // Console log removed
    }
    
    // API қателерін логтау. Қалдырып жіберетін жағдайлар (цикл/спам болдырмау үшін):
    //  - 401/403 — auth (бөлек логика)
    //  - 5xx (server-side/proxy) — қолданушы қатесі емес, спамдалмауы тиіс
    //  - /errors endpoint өзі — рекурсия болдырмау
    //  - ConnectionStatus polling (/auth/verify, /health) — әр 10 сек қайталанады
    //  - Network errors (response жоқ) — offline/timeout, спам болады
    const status = error.response?.status
    const url = error.config?.url || ''
    const isPolling = url.includes('/auth/verify') || url.includes('/health')
    const isServerError = typeof status === 'number' && status >= 500
    const isNetwork = !error.response
    const shouldSkip =
      status === 401 ||
      status === 403 ||
      url.includes('/errors') ||
      isPolling ||
      isServerError ||
      isNetwork
    if (!shouldSkip) {
      try { trackApiError(error, error.config) } catch (_) {}
    }
    
    // Handle common errors
    if (status === 401) {
      // Unauthorized - clear token and redirect to login
      sessionStorage.removeItem('korkem-auto-token')
      localStorage.removeItem('korkem-auto-token')
      delete api.defaults.headers.common['Authorization']
      
      // Only redirect if not already on auth pages
      if (!window.location.pathname.includes('/login') && 
          !window.location.pathname.includes('/register')) {
        window.location.href = resolveAppRoute('/login')
      }
    }

    const latestVersion = error.response?.headers?.['x-latest-app-version']
    if (latestVersion && appVersion && String(latestVersion).trim() !== String(appVersion).trim()) {
      triggerForcedClientRefresh(latestVersion)
    }
    
    return Promise.reject(error)
  }
)

export default api
